import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Camera, Upload, X, Check, Loader2, Receipt, AlertCircle, Image as ImageIcon, Calendar, SplitSquareHorizontal, AlertTriangle, ShoppingCart, FileText, Lock } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Switch } from './ui/switch';
import { useLanguage } from '../context/LanguageContext';
import { scanReceipt, createTransaction, analyzeReceiptForShopping, getSubscriptionUsageDetails } from '../lib/api';
import { formatCurrency, getCategoryColor } from '../lib/utils';
import api from '../lib/api';
import { toast } from 'sonner';

const INSTALLMENT_OPTIONS = [2, 3, 4, 6, 10, 12];

export default function OCRScannerModal({ open, onClose, onSuccess }) {
  const { t, language } = useLanguage();
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  
  const [step, setStep] = useState('upload'); // upload, scanning, review, date-warning, success, limit-reached
  const [selectedImage, setSelectedImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [scanResult, setScanResult] = useState(null);
  const [error, setError] = useState(null);
  const [editedData, setEditedData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // OCR limit state
  const [ocrLimit, setOcrLimit] = useState(null);
  const [ocrUsage, setOcrUsage] = useState(0);
  const [checkingLimit, setCheckingLimit] = useState(false);
  
  // Installment payment state
  const [isInstallment, setIsInstallment] = useState(false);
  const [totalInstallments, setTotalInstallments] = useState(3);
  
  // Store product prices for community comparison
  const [storeForComparison, setStoreForComparison] = useState(true);
  
  // Save as invoice option
  const [saveAsInvoice, setSaveAsInvoice] = useState(false);
  
  // Date warning state
  const [showDateWarning, setShowDateWarning] = useState(false);
  const [originalDate, setOriginalDate] = useState(null);
  
  // Success message state
  const [successMessage, setSuccessMessage] = useState('');
  const [xpGained, setXpGained] = useState(5);

  // Check OCR limit when modal opens
  useEffect(() => {
    if (open) {
      checkOCRLimit();
    }
  }, [open]);

  const checkOCRLimit = async () => {
    setCheckingLimit(true);
    try {
      const response = await getSubscriptionUsageDetails();
      const limits = response.data.limits;
      const usage = response.data.usage;
      
      setOcrLimit(limits.ocr_scans_per_month);
      setOcrUsage(usage.ocr_scans);
      
      // If limit is 0 or reached, show limit-reached step
      if (limits.ocr_scans_per_month === 0 || 
          (limits.ocr_scans_per_month !== -1 && usage.ocr_scans >= limits.ocr_scans_per_month)) {
        setStep('limit-reached');
      } else {
        setStep('upload');
      }
    } catch (err) {
      console.error('Error checking OCR limit:', err);
      // Allow to proceed if we can't check (backend will enforce)
      setStep('upload');
    } finally {
      setCheckingLimit(false);
    }
  };

  // Check if device has camera (mobile)
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  // Check if date is from previous month
  const isDateFromPreviousMonth = (dateString) => {
    if (!dateString) return false;
    const ticketDate = new Date(dateString);
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    const ticketMonth = ticketDate.getMonth();
    const ticketYear = ticketDate.getFullYear();
    
    // Return true if ticket is from any previous month
    return ticketYear < currentYear || (ticketYear === currentYear && ticketMonth < currentMonth);
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError(t('please_select_image'));
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError(t('image_too_large'));
      return;
    }

    setSelectedImage(file);
    setPreviewUrl(URL.createObjectURL(file));
    setError(null);
    
    // Start scanning
    await performScan(file);
  };

  const performScan = async (file) => {
    setStep('scanning');
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await scanReceipt(formData);
      
      if (response.data.success) {
        const data = response.data.data;
        const ticketDate = data.date || new Date().toISOString().split('T')[0];
        
        setScanResult(data);
        setEditedData({
          amount: data.amount || '',
          merchant: data.merchant || '',
          date: ticketDate,
          category: data.category_suggestion || 'divers',
        });
        
        // Check if date is from previous month
        if (isDateFromPreviousMonth(ticketDate)) {
          setOriginalDate(ticketDate);
          setShowDateWarning(true);
          setStep('date-warning');
        } else {
          setStep('review');
        }
      } else {
        setError(response.data.error || t('could_not_read_receipt'));
        setStep('upload');
      }
    } catch (err) {
      console.error('Scan error:', err);
      // Display the actual error message from the server if available
      const errorMessage = err.response?.data?.detail || err.response?.data?.error || t('scan_error');
      setError(errorMessage);
      setStep('upload');
    }
  };

  const handleDateChoice = (useCurrentMonth) => {
    if (useCurrentMonth) {
      // Use today's date
      setEditedData(prev => ({ ...prev, date: new Date().toISOString().split('T')[0] }));
    }
    // Otherwise keep original date
    setShowDateWarning(false);
    setStep('review');
  };

  const handleConfirm = async () => {
    if (!editedData?.amount) return;

    setIsSubmitting(true);
    try {
      console.log('[OCR] Creating transaction:', editedData);
      const response = await createTransaction({
        amount: parseFloat(editedData.amount),
        category: editedData.category,
        description: editedData.merchant,
        date: editedData.date ? new Date(editedData.date).toISOString() : undefined,
        payment_method: 'card',
        is_installment: isInstallment,
        total_installments: isInstallment ? totalInstallments : undefined,
      });
      console.log('[OCR] Transaction created successfully:', response.data);

      // Store product prices for community comparison if enabled
      if (storeForComparison && editedData.items && editedData.items.length > 0) {
        try {
          console.log('[OCR] Storing products for price comparison:', editedData.items.length);
          await analyzeReceiptForShopping({
            items: editedData.items,
            merchant: editedData.merchant,
            date: editedData.date
          });
          console.log('[OCR] Products stored for comparison');
        } catch (shoppingErr) {
          console.warn('[OCR] Could not store products for comparison:', shoppingErr);
          // Don't fail the whole operation if this fails
        }
      }

      // Save as invoice if enabled and we have the scanned image
      if (saveAsInvoice && selectedImage) {
        try {
          console.log('[OCR] Creating invoice from scanned receipt');
          const invoiceData = new FormData();
          invoiceData.append('file', selectedImage);
          invoiceData.append('product_name', editedData.merchant || 'Ticket de caisse');
          invoiceData.append('store_name', editedData.merchant || 'Commerce');
          invoiceData.append('purchase_date', editedData.date || new Date().toISOString().split('T')[0]);
          invoiceData.append('amount', editedData.amount.toString());
          invoiceData.append('category', editedData.category || 'divers');
          
          await api.post('/invoices/upload', invoiceData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          console.log('[OCR] Invoice created successfully');
        } catch (invoiceErr) {
          console.warn('[OCR] Could not save as invoice:', invoiceErr);
          // Don't fail the whole operation if this fails
        }
      }

      // Check for transaction limit warning
      if (response.data?.warning) {
        const warning = response.data.warning;
        const message = language === 'fr' ? warning.message_fr : language === 'pt' ? (warning.message_pt || warning.message_en) : warning.message_en;
        
        if (warning.type === 'transaction_limit_reached') {
          toast.error(message, { duration: 6000 });
        } else if (warning.type === 'transaction_limit_approaching') {
          toast.warning(message, { duration: 5000 });
        }
      }

      // Set success message based on response
      if (response.data.installment_group_id) {
        const installmentAmount = response.data.installment_amount;
        setSuccessMessage(t('installment_created').replace('{count}', totalInstallments));
        setXpGained(response.data.xp_gained || 10);
      } else {
        const productsStored = storeForComparison && editedData.items?.length > 0;
        const invoiceSaved = saveAsInvoice && selectedImage;
        let message = t('expense_added_exclaim');
        if (productsStored) {
          message += ' ' + t('prices_stored');
        }
        if (invoiceSaved) {
          message += ' ' + t('invoice_saved');
        }
        setSuccessMessage(message);
        setXpGained(response.data.xp_gained || 5);
      }

      setStep('success');
      
      // Wait for success animation, then close and refresh
      setTimeout(async () => {
        console.log('[OCR] Closing modal and calling onSuccess callback');
        handleClose();
        
        // Call onSuccess callback to refresh dashboard data
        if (onSuccess) {
          console.log('[OCR] Calling onSuccess callback now');
          await onSuccess();
        }
      }, 1500);
    } catch (err) {
      console.error('[OCR] Create transaction error:', err);
      setError(t('error_creating'));
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setStep('upload');
    setSelectedImage(null);
    setPreviewUrl(null);
    setScanResult(null);
    setEditedData(null);
    setError(null);
    setIsInstallment(false);
    setTotalInstallments(3);
    setStoreForComparison(true);
    setSaveAsInvoice(false);
    setShowDateWarning(false);
    setOriginalDate(null);
    setSuccessMessage('');
    setXpGained(5);
    setOcrLimit(null);
    setOcrUsage(0);
    setCheckingLimit(false);
    onClose();
  };

  const categories = [
    { id: 'alimentation', emoji: '🍔' },
    { id: 'transport', emoji: '🚗' },
    { id: 'shopping', emoji: '🛍️' },
    { id: 'loisirs', emoji: '🎮' },
    { id: 'sante', emoji: '💊' },
    { id: 'logement', emoji: '🏠' },
    { id: 'abonnements', emoji: '📱' },
    { id: 'divers', emoji: '📦' },
  ];
  
  // Calculate installment amount preview
  const installmentAmount = isInstallment && editedData?.amount 
    ? (parseFloat(editedData.amount) / totalInstallments).toFixed(2) 
    : null;

  // Format date for display
  const formatDateDisplay = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString(language === 'fr' ? 'fr-FR' : language === 'pt' ? 'pt-PT' : 'en-US', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Receipt className="w-5 h-5 text-primary" />
            {t('scan_receipt')}
          </DialogTitle>
        </DialogHeader>

        <AnimatePresence mode="wait">
          {/* Checking limit */}
          {checkingLimit && (
            <motion.div
              key="checking"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="py-12 text-center"
            >
              <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" />
              <p className="text-sm text-muted-foreground mt-2">
                {t('checking_quota')}
              </p>
            </motion.div>
          )}

          {/* Limit Reached Step */}
          {step === 'limit-reached' && !checkingLimit && (
            <motion.div
              key="limit-reached"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="py-6"
            >
              <div className="text-center space-y-4">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto">
                  <Lock className="w-8 h-8 text-orange-500" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">
                    {t('limit_reached')}
                  </h3>
                  <p className="text-sm text-muted-foreground mt-2">
                    {ocrLimit === 0 
                      ? t('ocr_not_available')
                      : t('scans_used').replace('{used}', ocrUsage).replace('{limit}', ocrLimit)
                    }
                  </p>
                </div>
                <div className="bg-muted/50 rounded-lg p-4">
                  <p className="text-sm">
                    {t('upgrade_for_ocr')}
                  </p>
                  <Button 
                    className="mt-3 w-full"
                    onClick={() => {
                      handleClose();
                      window.location.href = '/settings';
                    }}
                  >
                    {t('view_subscriptions_btn')}
                  </Button>
                </div>
                <Button variant="outline" onClick={handleClose} className="w-full">
                  {t('close')}
                </Button>
              </div>
            </motion.div>
          )}

          {/* Upload Step */}
          {step === 'upload' && !checkingLimit && (
            <motion.div
              key="upload"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="py-6"
            >
              {/* Hidden file inputs */}
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleFileSelect}
                className="hidden"
                data-testid="ocr-camera-input"
              />
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="hidden"
                data-testid="ocr-file-input"
              />

              <div className="space-y-4">
                {/* Camera Button (prominent on mobile) */}
                {isMobile && (
                  <button
                    onClick={() => cameraInputRef.current?.click()}
                    className="w-full aspect-[4/3] bg-gradient-to-br from-primary to-orange-500 rounded-2xl flex flex-col items-center justify-center gap-4 text-white shadow-lg active:scale-98 transition-transform"
                    data-testid="ocr-camera-button"
                  >
                    <div className="w-20 h-20 rounded-full bg-white/20 flex items-center justify-center">
                      <Camera className="w-10 h-10" />
                    </div>
                    <div className="text-center">
                      <p className="font-bold text-lg">
                        {t('take_photo')}
                      </p>
                      <p className="text-sm text-white/80">
                        {t('of_receipt')}
                      </p>
                    </div>
                  </button>
                )}

                {/* Desktop: Combined upload area */}
                {!isMobile && (
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full aspect-[4/3] border-2 border-dashed border-border rounded-2xl flex flex-col items-center justify-center gap-4 hover:border-primary/50 hover:bg-muted/50 transition-all"
                    data-testid="ocr-upload-area"
                  >
                    <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center">
                      <Upload className="w-8 h-8 text-primary" />
                    </div>
                    <div className="text-center">
                      <p className="font-medium">
                        {t('select_image')}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {t('image_formats')}
                      </p>
                    </div>
                  </button>
                )}

                {/* Gallery button for mobile */}
                {isMobile && (
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <ImageIcon className="w-4 h-4 mr-2" />
                    {t('choose_from_gallery')}
                  </Button>
                )}

                {error && (
                  <div className="flex items-center gap-2 p-3 bg-red-50 text-red-600 rounded-lg text-sm">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    {error}
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {/* Scanning Step */}
          {step === 'scanning' && (
            <motion.div
              key="scanning"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="py-12 text-center"
            >
              {previewUrl && (
                <div className="w-32 h-32 mx-auto mb-6 rounded-xl overflow-hidden border border-border">
                  <img src={previewUrl} alt="Receipt" className="w-full h-full object-cover" />
                </div>
              )}
              <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
              <p className="font-medium">
                {t('analyzing_receipt')}
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                {t('claude_extracting')}
              </p>
            </motion.div>
          )}

          {/* Date Warning Step */}
          {step === 'date-warning' && (
            <motion.div
              key="date-warning"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              className="py-6 space-y-4"
            >
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                    <AlertTriangle className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="font-medium text-amber-800">
                      {t('receipt_previous_month')}
                    </p>
                    <p className="text-sm text-amber-700 mt-1">
                      {t('receipt_date_warning').replace('{date}', formatDateDisplay(originalDate))}
                    </p>
                  </div>
                </div>
              </div>

              <p className="text-center font-medium">
                {t('which_date_to_use')}
              </p>

              <div className="grid grid-cols-1 gap-3">
                <Button
                  variant="default"
                  className="w-full h-auto py-4 flex-col items-start"
                  onClick={() => handleDateChoice(true)}
                  data-testid="use-current-date-btn"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Calendar className="w-4 h-4" />
                    <span className="font-bold">
                      {t('use_today_date')}
                    </span>
                  </div>
                  <span className="text-xs opacity-80">
                    {t('counted_in_current_month')}
                  </span>
                </Button>

                <Button
                  variant="outline"
                  className="w-full h-auto py-4 flex-col items-start"
                  onClick={() => handleDateChoice(false)}
                  data-testid="keep-original-date-btn"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Receipt className="w-4 h-4" />
                    <span className="font-bold">
                      {t('keep_receipt_date')}
                    </span>
                  </div>
                  <span className="text-xs opacity-80">
                    {formatDateDisplay(originalDate)}
                  </span>
                </Button>
              </div>
            </motion.div>
          )}

          {/* Review Step */}
          {step === 'review' && editedData && (
            <motion.div
              key="review"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="py-4 space-y-4"
            >
              {previewUrl && (
                <div className="w-full h-32 rounded-xl overflow-hidden border border-border">
                  <img src={previewUrl} alt="Receipt" className="w-full h-full object-cover" />
                </div>
              )}

              {/* Amount */}
              <div>
                <label className="text-sm font-medium mb-2 block">{t('amount')}</label>
                <div className="relative">
                  <Input
                    type="number"
                    value={editedData.amount}
                    onChange={(e) => setEditedData(prev => ({ ...prev, amount: e.target.value }))}
                    className="text-2xl font-bold text-center h-14 pr-8"
                    data-testid="ocr-amount-input"
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground">€</span>
                </div>
              </div>

              {/* Installment Payment Toggle */}
              <div className="bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-950/20 dark:to-blue-950/20 rounded-xl p-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <SplitSquareHorizontal className="w-5 h-5 text-purple-600" />
                    <span className="font-medium text-sm">
                      {t('installment_payment')}
                    </span>
                  </div>
                  <Switch
                    checked={isInstallment}
                    onCheckedChange={setIsInstallment}
                    data-testid="ocr-installment-toggle"
                  />
                </div>
                
                {/* Installment options */}
                <AnimatePresence>
                  {isInstallment && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden"
                    >
                      <div className="pt-3 space-y-2">
                        <div className="flex flex-wrap gap-2">
                          {INSTALLMENT_OPTIONS.map((num) => (
                            <button
                              key={num}
                              onClick={() => setTotalInstallments(num)}
                              className={`px-3 py-1.5 rounded-lg border-2 text-sm font-medium transition-all ${
                                totalInstallments === num
                                  ? 'border-purple-500 bg-purple-100 text-purple-700'
                                  : 'border-border hover:border-purple-300'
                              }`}
                              data-testid={`ocr-installment-${num}`}
                            >
                              {num}x
                            </button>
                          ))}
                        </div>
                        
                        {/* Preview */}
                        {installmentAmount && (
                          <p className="text-sm font-medium text-purple-600">
                            → {totalInstallments} × {installmentAmount}€/mois
                          </p>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Merchant */}
              <div>
                <label className="text-sm font-medium mb-2 block">
                  {t('merchant')}
                </label>
                <Input
                  value={editedData.merchant}
                  onChange={(e) => setEditedData(prev => ({ ...prev, merchant: e.target.value }))}
                  placeholder={t('merchant_name')}
                  data-testid="ocr-merchant-input"
                />
              </div>

              {/* Date */}
              <div>
                <label className="text-sm font-medium mb-2 block">{t('date')}</label>
                <Input
                  type="date"
                  value={editedData.date}
                  onChange={(e) => setEditedData(prev => ({ ...prev, date: e.target.value }))}
                  data-testid="ocr-date-input"
                />
              </div>

              {/* Category */}
              <div>
                <label className="text-sm font-medium mb-2 block">{t('category')}</label>
                <div className="grid grid-cols-4 gap-2">
                  {categories.map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => setEditedData(prev => ({ ...prev, category: cat.id }))}
                      className={`p-2 rounded-xl border-2 transition-all ${
                        editedData.category === cat.id
                          ? 'border-primary bg-primary/10'
                          : 'border-border hover:border-primary/50'
                      }`}
                      data-testid={`ocr-category-${cat.id}`}
                    >
                      <span className="text-xl block text-center">{cat.emoji}</span>
                      <span className="text-xs text-center block mt-1 truncate">
                        {t(`cat_${cat.id}`)}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Confidence Score */}
              {scanResult?.confidence && (
                <div className="flex items-center gap-2 p-2 bg-muted rounded-lg text-sm">
                  <span className="text-muted-foreground">
                    {t('confidence')} :
                  </span>
                  <span className={`font-medium ${
                    scanResult.confidence > 0.8 ? 'text-green-600' :
                    scanResult.confidence > 0.5 ? 'text-yellow-600' : 'text-red-600'
                  }`}>
                    {Math.round(scanResult.confidence * 100)}%
                  </span>
                </div>
              )}
              
              {/* Store for community price comparison */}
              {editedData.items && editedData.items.length > 0 && (
                <div className="flex items-center justify-between p-3 bg-blue-50 dark:bg-blue-950/30 rounded-lg">
                  <div className="flex items-center gap-2">
                    <ShoppingCart className="w-4 h-4 text-blue-600" />
                    <div>
                      <p className="text-sm font-medium text-blue-700 dark:text-blue-400">
                        {t('share_prices')}
                      </p>
                      <p className="text-xs text-blue-600/70">
                        {editedData.items.length} {t('products_detected_community')}
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={storeForComparison}
                    onCheckedChange={setStoreForComparison}
                  />
                </div>
              )}

              {/* Save as Invoice Option */}
              <div className="flex items-center justify-between p-3 bg-emerald-50 dark:bg-emerald-950/30 rounded-lg">
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-emerald-600" />
                  <div>
                    <p className="text-sm font-medium text-emerald-700 dark:text-emerald-400">
                      {t('save_as_invoice')}
                    </p>
                    <p className="text-xs text-emerald-600/70">
                      {t('keep_receipt_warranties')}
                    </p>
                  </div>
                </div>
                <Switch
                  checked={saveAsInvoice}
                  onCheckedChange={setSaveAsInvoice}
                  data-testid="ocr-save-as-invoice-toggle"
                />
              </div>

              {error && (
                <div className="flex items-center gap-2 p-3 bg-red-50 text-red-600 rounded-lg text-sm">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {error}
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setStep('upload')}
                  data-testid="ocr-retry-btn"
                >
                  {t('retry')}
                </Button>
                <Button
                  className="flex-1"
                  onClick={handleConfirm}
                  disabled={!editedData.amount || isSubmitting}
                  data-testid="ocr-confirm-btn"
                >
                  {isSubmitting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      {isInstallment 
                        ? t('create_installments').replace('{count}', totalInstallments)
                        : t('confirm')
                      }
                    </>
                  )}
                </Button>
              </div>
            </motion.div>
          )}

          {/* Success Step */}
          {step === 'success' && (
            <motion.div
              key="success"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="py-12 text-center"
            >
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-green-600" />
              </div>
              <p className="text-lg font-medium">
                {successMessage || (t('expense_added'))}
              </p>
              <p className="text-2xl font-bold text-primary mt-2">
                {isInstallment && editedData?.amount
                  ? `${totalInstallments} × ${(parseFloat(editedData.amount) / totalInstallments).toFixed(2)}€`
                  : formatCurrency(parseFloat(editedData?.amount || 0))
                }
              </p>
              <p className="text-sm text-muted-foreground mt-1">+{xpGained} XP</p>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
}
